import { injectable } from 'inversify';
import { Component } from '../../../concepts/objects';
import { ParticipantManager } from '../../canvas/participants/ParticipantManager';
import { resolve } from '../../Container';
import { DefineDimension, DefineSimpleDimension } from '../dataExport/addons/SimpleDimension';
import { FromFirstMatchExportAdapter } from '../dataExport/ExportAdapter';
import { ExportDimension, ExportValue } from '../dataExport/ExportDimension';
import { Sticky } from '../paper/Sticky';
import { Votable } from './Votable';
import { VoteIndex } from './VoteIndex';

@injectable()
export class VoteAggregateExportAdapter extends FromFirstMatchExportAdapter {

    @resolve(VoteIndex) private voteIndex!: VoteIndex;

    protected selector = (c: Component) => c.is(Votable);

    public readonly id = 'VoteAggregate';
    public readonly label = 'Votes';
    public readonly columns = [
        DefineSimpleDimension(
            Sticky,
            'Votes',
            'Adds a column that contains the total number of votes cast for each object.',
            (c: Component) => {
                const subject = c.as(Votable);
                if (!subject) return null;
                const votes = this.voteIndex.lookup('*', subject.id);
                return votes.size;
            }
        ),
    ];
}

@injectable()
export class VoteByParticipantExportAdapter extends FromFirstMatchExportAdapter {

    @resolve(ParticipantManager) private participants!: ParticipantManager;
    @resolve(VoteIndex) private voteIndex!: VoteIndex;

    protected selector = (c: Component) => c.is(Votable);

    public readonly id = 'VoteBreakdown';
    public readonly label = 'Votes';
    public readonly columns = [
        DefineDimension(
            'VoteBreakdown',
            'Votes By User',
            'Adds a column per participant that contains the total number of votes that they cast for each object.' +
            'Columns are only added for participants that cast at least one vote.',
            (type, params) => ExportDimension({
                id: 'VoteBreakdown',
                label: 'Votes By User',
                params,
                type,
                read: createReadWithScope(this.participants, this.voteIndex)
            }),
        )
    ];
}

const createReadWithScope = (participants: ParticipantManager, voteIndex: VoteIndex) =>
    function (this: ExportDimension, rows: Component[]) {

        const users = new Set<string>();
        const nameFor = (id: string) => participants.get(id)?.name ?? id;

        for (const c of rows) {
            const cvs = voteIndex.lookup('*', c.id);
            cvs.forEach(v => users.add(v.userId));
        }

        return Array.from(users).sort().map(u => {
            const head = [ExportValue([this.id, u], nameFor(u))];
            const body = rows.map(c =>
                ExportValue([this.id, u], Array.from(voteIndex.lookup('*', c.id)).filter(v => v.userId === u).length)
            );
            return head.concat(body);
        });
    };


